import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchData } from "../utils";
import * as endpoints from '../settings';
import { schemas } from './schemas';
import sortBy from "lodash.sortby";
import { setNotification } from "./notificationsSlice";
import { pickBy } from "lodash";

export const getOrgAliases = createAsyncThunk(
    "getOrgAliases",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_ORGS, {}, schemas.ALIASES);
            return sortBy(Object.values(response.entities.orgs), "title");
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const getJiraComponents = createAsyncThunk(
    "getJiraComponents",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_SUPPORT_ISSUES_COMPONENTS, {}, schemas.COMPONENTS);
            return Object.values(response.entities.components);
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const getContracts = createAsyncThunk(
    "getContracts",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_CONTRACTS, {}, schemas.CONTRACTS_LIST);
            return Object.values(response.entities.contracts);
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const getGrants = createAsyncThunk(
    "getGrants",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_GRANTS, {}, schemas.GRANTS_LIST);
            return Object.values(response.entities.grants);
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const getProducts = createAsyncThunk(
    "getProducts",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_PRODUCTS + "?limit=500", {}, schemas.PRODUCTS_LIST);
            return Object.values(response.entities.products)[0];
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

export const searchPackages = createAsyncThunk(
    "searchPackages",
    async (sysarch, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_PACKAGES_SEARCH, {
                method: "put",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: "",
                    sysarch: sysarch,
                    latestver: 0
                })
            }, schemas.SEARCH_PACKAGES);
            return Object.values(response.entities.packages);
        } catch (e) {
            dispatch(setNotification(e));
        }
        return rejectWithValue([]);
    }
);

const urls = {
    organisations: endpoints.API_ORG_DETAILS,
    users: endpoints.API_USER_DETAILS,
    grants: endpoints.API_ISSUE_GRANT,
    contracts: endpoints.API_ORG_CONTRACT,
    restrictions: endpoints.API_PACKAGE_RESTRICTIONS,
    prereq: endpoints.API_PACKAGE_PREREQ,
    contract: endpoints.API_CONTRACT
};

export const addItem = createAsyncThunk(
    "addItem",
    async({ entity, values }, { dispatch, getState, rejectWithValue }) => {
        try {
            let url = urls[entity],
            headers = { "Content-Type": "application/json" },
            method = ["prereq",  "restrictions"].includes(entity) ? "post" : "put";
            if (url) {
                if (Object.keys(getState().form.values).length && entity !== "grants" && entity !== "restrictions" && entity !== "prereq") url += "/update";
                const response = await fetchData(url, {
                    method: method,
                    headers: headers,
                    body: values
                }, {});
                dispatch(setNotification(response.result));
                return { entity: entity, ...response.result };
            };
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue()
        };
    }
);

const reportUrls = {
    contracts: endpoints.API_CONTRACTS_REPORT,
    packages: endpoints.API_PACKAGES_REPORT,
    users: endpoints.API_USERS_REPORT,
    organisations: endpoints.API_ORGS_REPORT,
    audit: {
        downloads: endpoints.API_AUDIT_REPORT + "/download",
        packages: endpoints.API_AUDIT_REPORT + "/packages",
        users: endpoints.API_AUDIT_REPORT + "/users",
        grants: endpoints.API_AUDIT_REPORT + "/user/grants",
        impersonations: endpoints.API_AUDIT_REPORT + "/impersonations",
        contracts: endpoints.API_AUDIT_REPORT + "/contracts",
        orgs: endpoints.API_AUDIT_REPORT + "/orgs"
    }
};


export const getReport = createAsyncThunk(
    "getReport",
    async({ page, tab, values }, { dispatch }) => {
        try {
            const url = page ? reportUrls[page][tab] : reportUrls[tab];

            if (url) {
                const response = await fetchData(url, {
                    method: "put",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(pickBy(values, o => o !== null))
                }, {});
                if (response.result && response.result.hasOwnProperty("url")) {
                    window.open(response.result.url);
                } else {
                    dispatch(setNotification({ message: "No data matching the conditions." }))
                };
            } else {
                dispatch(setNotification({ message: "Not implemented." }));
            }
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);


const initialState = {
    visible: false,
    aliases: [],
    components: [],
    contracts: [],
    packages: [],
    products: [],
    grants: [],
    loading: false,
    hasChanges: false,
    values: {}
};

const form = createSlice({
    name: 'form',
    initialState,
    reducers: {
        showForm: (state, action) => {
            if (action.payload) {
                state.visible = action.payload;
                state.values = {};
                state.hasChanges = false;
            } else {
                return {...initialState, products: state.products};
            }
        },
        hasChanged: (state, action) => {
            state.hasChanges = action.payload;
        },
        editValues: (state, action) => {
            state.values = action.payload.values;
            state.visible = action.payload.entity;
            state.hasChanges = false;
        }
    },
    extraReducers: {
        [getOrgAliases.pending]: (state) => {
            state.loading = true;
        },
        [getOrgAliases.fulfilled]: (state, action) => {
            state.aliases = action.payload;
            state.loading = false;
        },
        [getOrgAliases.rejected]: (state) => {
            state.loading = false;
        },
        [getJiraComponents.pending]: (state) => {
            state.loading = true;
        },
        [getJiraComponents.fulfilled]: (state, action) => {
            state.components = action.payload;
            state.loading = false;
        },
        [getJiraComponents.rejected]: (state) => {
            state.loading = false;
        },
        [addItem.pending]: (state) => {
            state.visible = false;
            state.hasChanges = false;
        },
        [addItem.fulfilled]: (state) => {
            state.values = {};
        },
        [getContracts.fulfilled]: (state, action) => {
            state.contracts = action.payload;
        },
        [getGrants.fulfilled]: (state, action) => {
            state.grants = action.payload;
        },
        [searchPackages.pending]: (state, action) => {
            state.loading = true;
            state.packages = [];
        },
        [searchPackages.fulfilled]: (state, action) => {
            state.packages = action.payload;
            state.loading = false;
        },
        [searchPackages.rejected]: (state, action) => {
            state.loading = false;
        },
        [getReport.pending]: (state, action) => ({
            ...initialState,
            products: state.products
        }),
        [getProducts.fulfilled]: (state, { payload }) => {
            state.products = payload;
        }
    }
});

export const { showForm, hasChanged, editValues } = form.actions;
export default form.reducer;